/**
 * Hook for managing conversation pin/unpin functionality.
 * Handles the pin mutation with toast notifications.
 */

import { usePinAgentConversationMutation } from '@saaga/shared/store/api.store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { useCallback } from 'react'
import { toast } from 'sonner'

export interface UsePinConversationReturn {
  isPinning: boolean
  togglePin: (conversationId: string, isCurrentlyPinned: boolean) => void
}

export const usePinConversation = (): UsePinConversationReturn => {
  const [pinConversation, { isLoading: isPinning }] = usePinAgentConversationMutation()

  const togglePin = useCallback(
    async (conversationId: string, isCurrentlyPinned: boolean): Promise<void> => {
      const newPinnedState = !isCurrentlyPinned

      try {
        await pinConversation({
          conversationId,
          payload: { is_pinned: newPinnedState },
        }).unwrap()

        trackEvent('conversation_pinned', { isPinned: newPinnedState })
        toast.success(newPinnedState ? 'Conversation pinned' : 'Conversation unpinned')
      } catch {
        toast.error(newPinnedState ? 'Failed to pin conversation' : 'Failed to unpin conversation')
      }
    },
    [pinConversation]
  )

  return {
    isPinning,
    togglePin,
  }
}
